import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useFileBlob } from '../hooks/useFileBlob';
import { ImagePreview } from './preview/ImagePreview';
import { PdfPreview } from './preview/PdfPreview';
import { VideoPreview } from './preview/VideoPreview';
import { AudioPreview } from './preview/AudioPreview';
import { TextPreview } from './preview/TextPreview';
import { UnsupportedPreview } from './preview/UnsupportedPreview';
import type { ProjectFileDto } from '../types';

type PreviewStrategy = 'image' | 'pdf' | 'video' | 'audio' | 'text' | 'none';

function getPreviewStrategy(mimeType: string | null): PreviewStrategy {
  if (!mimeType) return 'none';
  const m = mimeType.toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m.includes('pdf')) return 'pdf';
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  if (
    m.startsWith('text/') ||
    m.includes('javascript') ||
    m.includes('json') ||
    m.includes('xml') ||
    m.includes('html') ||
    m.includes('css')
  )
    return 'text';
  return 'none';
}

interface FilePreviewOverlayProps {
  file: ProjectFileDto;
  /** Ordered list of previewable files for prev/next navigation */
  files: ProjectFileDto[];
  onClose: () => void;
  onNavigate: (file: ProjectFileDto) => void;
  onDelete: (fileId: string) => void;
  onMove: (fileId: string) => void;
}

function PreviewContent({ file }: { file: ProjectFileDto }) {
  const strategy = getPreviewStrategy(file.mimeType);
  const needsBlob = strategy !== 'none';
  const { objectUrl, blob, isLoading, error } = useFileBlob(
    needsBlob ? (file.storageKey ?? null) : null,
  );

  if (needsBlob && isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Skeleton className="h-[60%] w-[70%] rounded-lg bg-white/10" />
      </div>
    );
  }

  if (needsBlob && !isLoading && error) return <UnsupportedPreview reason="error" />;

  if (strategy === 'image' && objectUrl) return <ImagePreview src={objectUrl} />;
  if (strategy === 'pdf' && objectUrl) return <PdfPreview src={objectUrl} />;
  if (strategy === 'video' && objectUrl)
    return <VideoPreview src={objectUrl} mimeType={file.mimeType} />;
  if (strategy === 'audio' && objectUrl)
    return <AudioPreview src={objectUrl} mimeType={file.mimeType} fileName={file.fileName} />;
  if (strategy === 'text' && blob) return <TextPreview blob={blob} />;

  return <UnsupportedPreview />;
}

export function FilePreviewOverlay({ file, files, onClose, onNavigate }: FilePreviewOverlayProps) {
  const { t } = useTranslation('projectFiles');

  const currentIndex = files.findIndex((f) => f.id === file.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex !== -1 && currentIndex < files.length - 1;

  // Keyboard navigation
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && hasPrev) {
        onNavigate(files[currentIndex - 1]);
      } else if (e.key === 'ArrowRight' && hasNext) {
        onNavigate(files[currentIndex + 1]);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, onNavigate, hasPrev, hasNext, files, currentIndex]);

  // Prevent body scroll while overlay is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const overlay = (
    <div className="fixed inset-0 z-50 bg-neutral-950/95" data-testid="file-preview-overlay">
      {/* Floating close button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-3 top-3 z-20 size-9 rounded-full bg-white/10 text-white/80 hover:bg-white/20 hover:text-white backdrop-blur-sm"
            onClick={onClose}
            aria-label={t('preview.close')}
            data-testid="file-preview-close"
          >
            <X className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">{t('preview.close')}</TooltipContent>
      </Tooltip>

      {/* Content area with prev/next navigation */}
      <div className="flex h-full items-center">
        {/* Prev arrow */}
        <div className="flex w-16 shrink-0 justify-center">
          {hasPrev && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-10 rounded-full bg-white/10 text-white/80 hover:bg-white/20 hover:text-white backdrop-blur-sm"
                  onClick={() => onNavigate(files[currentIndex - 1])}
                  aria-label={t('preview.previous')}
                  data-testid="file-preview-prev"
                >
                  <ChevronLeft className="size-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{t('preview.previous')}</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Preview */}
        <div className="flex flex-1 overflow-hidden h-full">
          <PreviewContent file={file} />
        </div>

        {/* Next arrow */}
        <div className="flex w-16 shrink-0 justify-center">
          {hasNext && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-10 rounded-full bg-white/10 text-white/80 hover:bg-white/20 hover:text-white backdrop-blur-sm"
                  onClick={() => onNavigate(files[currentIndex + 1])}
                  aria-label={t('preview.next')}
                  data-testid="file-preview-next"
                >
                  <ChevronRight className="size-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">{t('preview.next')}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
